import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartDataSets, ChartOptions } from "chart.js";
import { BaseChartDirective, Color, Label } from "ng2-charts";
import { SubSink } from "subsink";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ZoneComponent } from "@/shared-module/components";
import { RocePoint, RoceChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";
import * as pluginAnnotations from "chartjs-plugin-annotation";
import * as _ from "lodash";

@Component({
  selector: "app-rocechart",
  templateUrl: "rocechart.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class RoceChartComponent implements ZoneComponent, OnInit {
  @ViewChild("template", { static: true }) template;
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;
  @Input() data: RoceChartDatasource;
  @Input() width: number;
  @Input() needpaddingLeft: boolean;
  @Input() isZoom: boolean;
  subs = new SubSink();

  public lineChartData: ChartDataSets[] = [];
  public lineChartLabels: Label[] = [];
  public lineChartColors: Color[] = [];
  public lineChartLegend = true;
  public lineChartType = "line";
  public chartPlugins = [pluginAnnotations, ChartDataLabels];

  minY = 0.0;
  maxY = 3.0;
  minX = -0.25;
  maxX = 0.25;

  public lineChartOptions: ChartOptions & { annotation: any } = {
    aspectRatio: 1.4,
    responsive: true,
    annotation: {
      annotations: [
        {
          type: "line",
          mode: "vertical",
          scaleID: "x-axis-0",
          value: "0",
          borderWidth: 2,
        },
      ],
    },
    plugins: {
      datalabels: {
        display: true,
        align(context) {
          // tslint:disable-next-line:no-string-literal
          return "end";
        },
        color(context) {
          const value = context.dataset.data[context.dataIndex];
          return value["color"];
        },
        font: {
          weight: "bold",
        },
        formatter(value, context) {
          // tslint:disable-next-line:no-string-literal
          if (context.dataset.data.length === 1) return value.text;
          return null;
        },
        offset: 2,
        padding: 0,
      },
    },
    tooltips: {
      // Disable the on-canvas tooltip
      enabled: true,
      callbacks: {
        title: (items, data) => {
          if (
            data.datasets[items[0].datasetIndex].data[items[0].index][
              "serieName"
            ] != null
          )
            return (
              data.datasets[items[0].datasetIndex].data[items[0].index][
                "serieName"
              ] +
              ": " +
              data.datasets[items[0].datasetIndex].data[items[0].index]["text"]
            );
          return null;
        },
        label: (tooltipItem, data) => {
          if (
            tooltipItem.datasetIndex === 0 ||
            tooltipItem.datasetIndex === 1
          ) {
            var x =
              data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index][
                "x"
              ];
            var y =
              data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index][
                "y"
              ];
            return [
              this.data?.isoRoceXAxisLabel +
                ": " +
                Math.round(x * 1000) / 10 +
                "%",
              this.data?.isoRoceYAxisLabel + ": " + Math.round(y * 100) / 100,
            ];
          }
          return null;
        },
      },
    },
    scales: {
      gridLines: {
        drawBorder: false,
        display: true,
      },
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          type: "linear",
          scaleLabel: {
            display: true,
          },
          gridLines: {
            drawBorder: false,
            zeroLineWidth: 2,
            borderDash: [2, 5],
            display: true,
          },
          ticks: {
            beginAtZero: true,
            stepSize: 0.1,
            max: 0.2,
            min: -0.2,
            callback: function (value: number, index, values) {
              return Math.round(value * 100) + "%";
            },
          },
        },
      ],
      yAxes: [
        {
          id: "y-axis-0",
          type: "linear",
          scaleLabel: {
            display: true,
          },
          gridLines: {
            zeroLineWidth: 2,
            drawBorder: false,
            borderDash: [2, 5],
            display: true,
          },
          ticks: {
            min: 0,
            stepSize: 0.5,
            max: 1.0,
          },
        },
      ],
    },
  };

  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);

    this.lineChartData = [];
    this.lineChartColors = [];

    this.data.rocePoints.forEach((rc) => {
      this.lineChartData.push(this.getPoint(rc));
      this.lineChartColors.push(this.getColor(rc.color));
    });
    this.lineChartData.push(this.getDataSet(0.05));
    this.lineChartData.push(this.getDataSet(0.1));
    this.lineChartData.push(this.getDataSet(0.15));

    this.lineChartColors.push(this.getColor("#CCCCCC"));
    this.lineChartColors.push(this.getColor("#AAAAAA"));
    this.lineChartColors.push(this.getColor("#888888"));

    this.data.rocePoints.forEach((rc, idx) => {
      this.lineChartData[idx].data[0] = rc;
    });

    var minY = Math.min(...this.data.rocePoints.map((elt) => elt.y));
    var maxY = Math.max(...this.data.rocePoints.map((elt) => elt.y));
    var minX = Math.min(...this.data.rocePoints.map((elt) => elt.x));
    var maxX = Math.max(...this.data.rocePoints.map((elt) => elt.x));

    const maxAbsY = Math.max(Math.abs(minY), Math.abs(maxY));
    const maxAbsX = Math.max(Math.abs(minX), Math.abs(maxX));

    var optimalMinX = -maxAbsX;
    var optimalMaxX = maxAbsX;
    var optimalMinY = 0;
    var optimalMaxY = 0;

    if (minY < 0) optimalMinY = -maxAbsY;
    else optimalMinY = 0;

    if (maxY > 0) optimalMaxY = maxAbsY;
    else optimalMaxY = 0;

    this.lineChartOptions.scales.xAxes[0].scaleLabel.labelString =
      this.data.isoRoceXAxisLabel;
    this.lineChartOptions.scales.yAxes[0].scaleLabel.labelString =
      this.data.isoRoceYAxisLabel;
    this.minY = Math.floor(optimalMinY * 1.05);
    this.maxY = Math.ceil(optimalMaxY * 1.05);
    this.minX = this.getRoundedXAxeValue(optimalMinX * 1.05);
    this.maxX = this.getRoundedXAxeValue(optimalMaxX * 1.05);

    this.lineChartOptions.scales.xAxes[0].ticks.min = this.minX;
    this.lineChartOptions.scales.xAxes[0].ticks.max = this.maxX;
    this.lineChartOptions.scales.yAxes[0].ticks.min = this.minY;
    this.lineChartOptions.scales.yAxes[0].ticks.max = this.maxY;

    this.lineChartData = [];
    this.data.rocePoints.forEach((rc) => {
      this.lineChartData.push(this.getPoint(rc));
    });

    this.data.lines.forEach((line) => {
      this.lineChartData.push(this.getDataSet(line));
    });
  }

  getRoundedXAxeValue(value: number): number {
    var modulo5 = Math.abs(Math.round(value * 100)) % 5;
    var rounded = Math.abs(Math.round(value * 100)) + (5 - modulo5);
    var ret = rounded / 100.0;
    if (value < 0) return -ret;
    return ret;
  }

  getRoundedYAxeValue(value: number): number {
    return Math.ceil(value);
  }

  getColor(color: string): any {
    return {
      // grey
      backgroundColor: color,
      borderColor: color,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: color,
      borderWidth: 1,
    };
  }

  getDataSet(roce: number): any {
    const minX = 0;
    const maxX = this.maxX;

    const nbSteps = 100;
    const serie = [];

    for (let i = 0; i < nbSteps; i++) {
      const x = minX + ((maxX - minX) / nbSteps) * i;
      if (x > 0) {
        const y = roce / x;
        serie.push({ x: x, y: y });
      }
    }

    // tslint:disable-next-line:object-literal-shorthand
    return {
      data: serie,
      label: this.data?.legendTitle + " " + roce * 100 + "%",
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
    };
  }

  getPoint(data: RocePoint): any {
    const serie = [data];

    // tslint:disable-next-line:object-literal-shorthand
    return { data: serie, label: data.serieName, fill: false, pointRadius: 5 };
  }

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }
}
